import { useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { SalesTrendData, SalesTrendPattern } from '@/lib/types/ds/prediction-error-evaluation';
import type { SalesTrendMode } from './SalesTrendModeToggle';
import { SalesTrendModeToggle } from './SalesTrendModeToggle';
import {
  COMMON_ACTUAL_KEY,
  SERIES_COLORS,
  commonPredKey,
  commonSkuCoverage,
  defaultPatternKey,
  isCommonModeAvailable,
  patternKey,
  patternLabel,
  selectEntry,
  toChartRows,
  toCommonChartRows,
} from './salesTrendChartData';

/** 基準日の参照線・凡例で使う色（ErrorHistogramChart の参照線と同色） */
const TARGET_DATE_COLOR = '#ef4444';

/** 共通 SKU モードの実績カーブの色。パターン別の系列色と混同させないため中間色にする */
const COMMON_ACTUAL_COLOR = '#334155';

/** 表示中パターン1件分の描画メタ情報 */
interface VisibleSeries {
  key: string;
  label: string;
  color: string;
  skuCount: number;
  /** 現在のモードでの予測系列 dataKey */
  predKey: string;
}

/** {@link SalesTrendChart} の Props */
export interface SalesTrendChartProps {
  /** 基準日ごとの累積推移データ（result_data.sales_trend） */
  data: SalesTrendData;
  /** 表示する基準日。未指定・不一致なら先頭エントリを表示する */
  selectedTargetDate?: string;
}

/**
 * 販売数の実績 vs 予測を累積で比較する折れ線グラフ。
 *
 * 実績を実線・予測を同色の破線で描き、基準日で予測が実績カーブから分岐する。
 * 表示するパターン（train_term / tolerance / max_data_count の組）は内部 state で
 * トグルし、既定は基準日内ベスト1本、同時表示は {@link SERIES_COLORS} の本数まで。
 *
 * 母集団モードは「各パターンの母集団」（既定）と「共通 SKU のみ」（全パターンの積集合）を
 * トグルでき、共通 SKU モードでは実績カーブが全パターン共有の1本になる。
 */
export function SalesTrendChart({
  data,
  selectedTargetDate,
}: SalesTrendChartProps): React.ReactElement | null {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [mode, setMode] = useState<SalesTrendMode>('per-pattern');

  const entry = selectEntry(data, selectedTargetDate);
  if (!entry) return null;

  const commonAvailable = isCommonModeAvailable(entry);
  // 共通 SKU データを持たない基準日に切り替わったら state ごと既定モードへ落とす。
  // 実効モードの算出だけだと、共通データを持つ基準日に戻したとき勝手に共通モードへ復帰してしまう
  if (mode === 'common' && !commonAvailable) {
    setMode('per-pattern');
  }
  const isCommonMode = mode === 'common' && commonAvailable;

  const patternByKey = new Map(entry.patterns.map((pattern) => [patternKey(pattern), pattern]));
  // 基準日を切り替えても残っているキーは保持し、1つも残らないときだけ既定（ベスト）に戻す
  const retainedKeys = selectedKeys.filter((key) => patternByKey.has(key));
  const fallbackKey = defaultPatternKey(entry);
  const activeKeys =
    retainedKeys.length > 0 ? retainedKeys : fallbackKey !== undefined ? [fallbackKey] : [];

  const visiblePatterns = activeKeys
    .map((key) => patternByKey.get(key))
    .filter((pattern): pattern is SalesTrendPattern => pattern !== undefined);
  const rows = isCommonMode
    ? toCommonChartRows(entry, visiblePatterns)
    : toChartRows(entry, visiblePatterns);
  const series: VisibleSeries[] = visiblePatterns.map((pattern, index) => ({
    key: patternKey(pattern),
    label: patternLabel(pattern),
    color: SERIES_COLORS[index % SERIES_COLORS.length],
    skuCount: pattern.sku_count,
    predKey: isCommonMode ? commonPredKey(pattern) : `${patternKey(pattern)}__pred`,
  }));
  const coverage = isCommonMode ? commonSkuCoverage(entry) : undefined;

  const atSeriesLimit = activeKeys.length >= SERIES_COLORS.length;
  /** 押しても何も起きないボタンを作らないため、不可の理由を disabled で表現する */
  const isToggleDisabled = (key: string): boolean =>
    activeKeys.includes(key) ? activeKeys.length <= 1 : atSeriesLimit;

  // 「1本以上 SERIES_COLORS.length 本以下」の不変条件は disabled と二重になるが、
  // プログラム的な click でも崩れないようここにも残す
  const togglePattern = (key: string): void => {
    if (activeKeys.includes(key)) {
      if (activeKeys.length <= 1) return;
      setSelectedKeys(activeKeys.filter((k) => k !== key));
      return;
    }
    if (atSeriesLimit) return;
    setSelectedKeys([...activeKeys, key]);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          販売数 実績 vs 予測 累積推移（基準日: {entry.target_date}）
        </CardTitle>
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
          <span>
            横軸: 基準日 −{entry.train_days}日 〜 +{entry.eval_days}日（学習期間側は最大365日）
          </span>
          <span>実線 = 実績累積</span>
          <span>破線 = 予測累積</span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-0 w-4 border-t-2 border-dashed"
              style={{ borderColor: TARGET_DATE_COLOR }}
            />
            基準日
          </span>
          {/* disabled なボタンは title が出ないため、制約はここで説明する */}
          <span>同時表示は最大{SERIES_COLORS.length}本（表示中が1本のときは解除できません）</span>
        </div>
      </CardHeader>
      <CardContent>
        {commonAvailable && (
          <SalesTrendModeToggle mode={mode} coverage={coverage} onChange={setMode} />
        )}
        <div className="flex flex-wrap gap-2">
          {entry.patterns.map((pattern) => {
            const key = patternKey(pattern);
            const selected = activeKeys.includes(key);
            return (
              <Button
                key={key}
                size="sm"
                variant={selected ? 'default' : 'outline'}
                aria-pressed={selected}
                disabled={isToggleDisabled(key)}
                onClick={() => togglePattern(key)}
              >
                {patternLabel(pattern)}
              </Button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
          {coverage && (
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-0 w-4 border-t-2"
                style={{ borderColor: COMMON_ACTUAL_COLOR }}
                aria-hidden
              />
              共通 SKU 実績 ({coverage.commonSkuCount.toLocaleString()} SKU)
            </span>
          )}
          {/* カバレッジ差を隠さないため、パターン別の母集団は共通 SKU モードでも出す */}
          {series.map((s) => (
            <span key={s.key} className="flex items-center gap-1">
              {!isCommonMode && (
                <span
                  className="inline-block h-0 w-4 border-t-2"
                  style={{ borderColor: s.color }}
                  aria-hidden
                />
              )}
              <span
                className="inline-block h-0 w-4 border-t-2 border-dashed"
                style={{ borderColor: s.color }}
                aria-hidden
              />
              {s.label} ({s.skuCount.toLocaleString()} SKU)
            </span>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={rows} margin={{ top: 5, right: 30, left: 20, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              interval="preserveStartEnd"
              tickFormatter={(value: string) => value.slice(5)}
            />
            <YAxis
              tickFormatter={(value: number) => value.toLocaleString()}
              tick={{ fontSize: 11 }}
              label={{ value: '累積販売数', angle: -90, position: 'insideLeft', offset: -5 }}
            />
            <Tooltip formatter={(value: number, name: string) => [value.toLocaleString(), name]} />
            <ReferenceLine
              x={entry.target_date}
              stroke={TARGET_DATE_COLOR}
              strokeDasharray="5 5"
              label={{ value: '基準日', position: 'top', fontSize: 10, fill: TARGET_DATE_COLOR }}
            />
            {/* 共通 SKU モードの実績は全パターン共有なので1本だけ描く */}
            {isCommonMode ? (
              <Line
                key={COMMON_ACTUAL_KEY}
                type="monotone"
                dataKey={COMMON_ACTUAL_KEY}
                name="共通 SKU 実績"
                stroke={COMMON_ACTUAL_COLOR}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ) : (
              series.map((s) => (
                <Line
                  key={`${s.key}__actual`}
                  type="monotone"
                  dataKey={`${s.key}__actual`}
                  name={`${s.label} 実績`}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              ))
            )}
            {series.map((s) => (
              <Line
                key={s.predKey}
                type="monotone"
                dataKey={s.predKey}
                name={`${s.label} 予測`}
                stroke={s.color}
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={false}
                isAnimationActive={false}
                // 基準日より前は null なので線を繋がない（実績カーブから分岐させる）
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
